import type { BreadcrumbProps } from '@snapchat/snap-design-system-marketing/src/components/Breadcrumbs/Breadcrumb';

import type { BreadcrumbsDataProps } from './types';

/**
 * Transforms breadcrumb data into a structured array of `BreadcrumbProps`.
 *
 * @param {BreadcrumbsDataProps | undefined} slugData - The breadcrumb data from Contentful.
 * @returns {BreadcrumbProps[] | undefined} An array of structured breadcrumbs or undefined if no
 *   valid data is provided.
 */
export const structureBreadcrumbs = (slugData?: BreadcrumbsDataProps) => {
  if (!slugData) return undefined;

  // Do not create breadcrumbs if there is only one breadcrumb or if the text is missing
  const parent = slugData.slugParent;
  const currentBreadcrumbText = slugData.breadcrumbText;
  if (!parent || !currentBreadcrumbText) return undefined;

  const result: BreadcrumbProps[] = [];

  if (slugData.slugParent?.slugParent?.breadcrumbText) {
    result.push({
      slug: slugData.slugParent.slugParent?.slug
        ? `/${slugData.slugParent.slugParent.slug}`
        : undefined,
      breadcrumbText: slugData.slugParent.slugParent.breadcrumbText,
    });
  }

  if (slugData?.slugParent?.breadcrumbText) {
    result.push({
      slug: slugData.slugParent?.slug ? `/${slugData.slugParent.slug}` : undefined,
      breadcrumbText: slugData.slugParent.breadcrumbText,
    });
  }

  if (slugData.breadcrumbText) {
    result.push({
      slug: slugData?.slug ? `/${slugData.slug}` : undefined,
      breadcrumbText: slugData.breadcrumbText,
    });
  }

  return result.length > 1 ? result : undefined;
};

/**
 * Generates a JSON-LD schema for breadcrumbs based on the provided breadcrumb data.
 *
 * @param {BreadcrumbsDataProps | undefined} slugData - The breadcrumb data from Contentful.
 * @returns {object | undefined} A JSON-LD schema object for breadcrumbs or undefined if no valid
 *   data is provided.
 */
export const createBreadcrumbSchema = (slugData?: BreadcrumbsDataProps) => {
  if (typeof window === 'undefined') return;

  const breadcrumbs = structureBreadcrumbs(slugData);
  if (!breadcrumbs?.length) return;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb.breadcrumbText,
      item: breadcrumb.slug ? `${window.location.origin}${breadcrumb.slug}` : undefined,
    })),
  };
};
